import type { DateTime } from 'luxon';
import { type ChangeEvent, createContext, type FC, type MouseEvent, type ReactElement, useState } from 'react';
import { usePagination } from '../../content/hooks';
import type { ClientModel, GasCopReportData, GasCopSortingFields, Order, VenueModel } from '../../models';

export type GasCopExpiryWindow = '1' | '3' | '6' | 'all' | 'custom';

interface IReportsContext {
  loading: boolean;
  setLoading: (val: boolean) => void;
  dataUpdate: boolean;
  toggleUpdate: () => void;
  data: GasCopReportData[];
  setData: (val: GasCopReportData[]) => void;
  page: number;
  setPage: (val: number) => void;
  limit: number;
  setLimit: (val: number) => void;
  order: Order;
  setOrder: (val: Order) => void;
  orderBy: GasCopSortingFields;
  setOrderBy: (val: GasCopSortingFields) => void;
  handlePageChange: (_event: any, newPage: number) => void;
  handleLimitChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRequestSort: (_event: MouseEvent, property: GasCopSortingFields) => void;
  count: number;
  setCount: (val: number) => void;
  fromDate: DateTime | null;
  setFromDate: (val: DateTime | null) => void;
  toDate: DateTime | null;
  setToDate: (val: DateTime | null) => void;
  client: ClientModel | null;
  setClient: (val: ClientModel | null) => void;
  venues: VenueModel[];
  setVenues: (val: VenueModel[]) => void;
  expiryWindow: GasCopExpiryWindow;
  setExpiryWindow: (val: GasCopExpiryWindow) => void;
}

export const GasCopReportContext = createContext<IReportsContext>({} as IReportsContext);

export const GasCopReportProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [dataUpdate, setDataUpdate] = useState(false);
  const toggleUpdate = (): void => setDataUpdate(!dataUpdate);

  const {
    page,
    setPage,
    limit,
    setLimit,
    count,
    setCount,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    handlePageChange,
    handleLimitChange,
    handleRequestSort
  } = usePagination<GasCopSortingFields>(toggleUpdate, 'gasCopExpiryDate', 'asc');

  const [data, setData] = useState<GasCopReportData[]>([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState<DateTime | null>(null);
  const [toDate, setToDate] = useState<DateTime | null>(null);
  const [client, setClient] = useState<ClientModel | null>(null);
  const [venues, setVenues] = useState<VenueModel[]>([]);
  const [expiryWindow, setExpiryWindow] = useState<GasCopExpiryWindow>('all');

  return (
    <GasCopReportContext.Provider
      value={{
        data,
        setData,
        page,
        setPage,
        limit,
        setLimit,
        count,
        setCount,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        handlePageChange,
        handleLimitChange,
        handleRequestSort,
        loading,
        setLoading,
        dataUpdate,
        toggleUpdate,
        fromDate,
        setFromDate,
        toDate,
        setToDate,
        client,
        setClient,
        venues,
        setVenues,
        expiryWindow,
        setExpiryWindow
      }}
    >
      {children}
    </GasCopReportContext.Provider>
  );
};
